import React, { useState, useEffect } from 'react';
import { LogOut, User, Key, Shield } from 'lucide-react';
import LoginPage from './LoginPage';
import CustomerVehicleManagement from './CustomerVehicleManagement';


//const API_BASE_URL = 'http://localhost/vehicle-api';
//const API_BASE_URL = 'http://109.199.120.141:8083/api';
const API_BASE_URL = 'https://crm.securewheels.pk/api';
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in (on component mount)
  useEffect(() => {
    checkSession();
  }, []);

  // Normalize user object to handle varying API response shapes
  const normalizeUser = (userData) => {
    if (!userData) return null;

    // If the API wrapped the user inside a nested key, unwrap it first
    const raw =
      userData.user       ||
      userData.data       ||
      userData.employee   ||
      userData.account    ||
      userData;

    console.log('[AUTH] Raw user object keys:', Object.keys(raw));
    console.log('[AUTH] Raw user object:', raw);

    const full_name =
      raw.full_name      ||
      raw.fullName       ||
      raw.name           ||
      raw.display_name   ||
      raw.displayName    ||
      raw.employee_name  ||
      raw.emp_name       ||
      raw.first_name     ||   // in case first/last are separate
      raw.fname          ||
      raw.username       ||
      raw.user_name      ||
      raw.login          ||
      'User';

    // If only first_name is set, try to combine with last_name
    const combined =
      (raw.first_name && raw.last_name)
        ? `${raw.first_name} ${raw.last_name}`.trim()
        : null;

    const resolved_name = (combined && combined !== full_name) ? combined : full_name;

    const role =
      typeof raw.role === 'object'
        ? (raw.role?.name || raw.role?.title || 'user')
        : (raw.role || raw.role_name || raw.roleName || 'user');

    console.log('[AUTH] Resolved full_name:', resolved_name, '| role:', role);
    return { ...raw, full_name: resolved_name, role };
  };

  const checkSession = () => {
    try {
      const storedAuth = localStorage.getItem('isAuthenticated');
      const storedUser = localStorage.getItem('user');

      if (storedAuth === 'true' && storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
        const parsed = JSON.parse(storedUser);
        const userData = normalizeUser(parsed);
        setCurrentUser(userData);
        setIsAuthenticated(true);
      } else {
        // Only clear if there's actually corrupt/partial data — don't wipe if both are simply null
        if (storedAuth !== null || storedUser !== null) {
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('user');
        }
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('[AUTH] Session restore failed:', error);
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (user) => {
    if (!user) {
      console.error('[AUTH] handleLoginSuccess called with no user! Fix LoginPage to pass user object to onLoginSuccess(user)');
      return;
    }
    const normalized = normalizeUser(user);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify(normalized));
    setCurrentUser(normalized);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'logout' })
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  };

  // Show loading spinner while checking session
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Show main application — user menu is merged into the app's top nav bar
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Main Application */}
      <CustomerVehicleManagement
        currentUser={currentUser}
        onLogout={handleLogout}
        onChangePassword={() => setShowChangePassword(true)}
      />

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}

// Change Password Modal Component
function ChangePasswordModal({ onClose, currentUser }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'change_password',
          user_id: currentUser?.id,
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Password changed successfully!');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to change password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
      console.error('Change password error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">Change Password</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all"
              required
              disabled={loading}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl border-2 border-slate-300 hover:bg-slate-50 transition-colors font-semibold"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors font-semibold disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
